import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import {
  subscribeToNotifications,
  markNotificationRead as markRead,
  markAllNotificationsRead as markAllRead,
  deleteNotification as deleteNotif
} from '../services/firestore'

const NotificationContext = createContext(null)

export function NotificationProvider({ children }) {
  const { profile } = useAuth()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (!profile?.id) {
      setNotifications([])
      setUnreadCount(0)
      return
    }

    const unsubscribe = subscribeToNotifications(profile.id, (notifs) => {
      setNotifications(notifs)
      setUnreadCount(notifs.filter(n => !n.read).length)
    })

    return () => unsubscribe()
  }, [profile?.id])

  const markNotificationRead = useCallback(async (id) => {
    await markRead(id)
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    setUnreadCount(prev => Math.max(0, prev - 1))
  }, [])

  const markAllNotificationsRead = useCallback(async () => {
    if (!profile?.id) return
    await markAllRead(profile.id)
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    setUnreadCount(0)
  }, [profile?.id])

  const removeNotification = useCallback(async (id) => {
    const notif = notifications.find(n => n.id === id)
    await deleteNotif(id)
    setNotifications(prev => prev.filter(n => n.id !== id))
    if (notif && !notif.read) setUnreadCount(prev => Math.max(0, prev - 1))
  }, [notifications])

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      markNotificationRead,
      markAllNotificationsRead,
      deleteNotification: removeNotification
    }}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) throw new Error('useNotifications must be used within NotificationProvider')
  return context
}
